import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { BranchesService } from 'src/app/_services/branches/logic/branches.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { BranchFunctionGroup } from 'src/app/_model/branch-function-group';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';

import { BranchFunctionGroupsService } from '../branch-function-groups.service';
import { BranchFunctionGroupsDaoService } from '../../dao/branch-function-groups-dao.service';
import { APILevel } from 'src/app/_services/sync/api-level';

/**
 * Loads the ims/Branch <-> ims/FunctionGroup mapping for the branches
 * currently available in the app
 */
@Injectable()
export class BranchFunctionGroupsLogicService implements BranchFunctionGroupsService {
  public branchFunctionGroups$ = new BehaviorSubject<BranchFunctionGroup[]>([]);
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private remoteActionService: RemoteActionService,
    private branchFunctionGroupsDaoService: BranchFunctionGroupsDaoService,
    private authenticationService: AuthenticationService,
    private branchesService: BranchesService,
  ) {
    this.initialize();
  }

  // Resetable
  public async reset() {
    // Unlike clear(), reset() also wipes locally created (not-yet-synced) mappings.
    await this.branchFunctionGroupsDaoService.reset();
    const remaining = await this.branchFunctionGroupsDaoService.getBranchFunctionGroups();
    this.branchFunctionGroups$.next(remaining);
  }

  public async clear() {
    // Only truncates the synced table (see DAO) - reload so locally created
    // mappings that survive the clear stay visible in the BehaviorSubject.
    await this.branchFunctionGroupsDaoService.clear();
    const remaining = await this.branchFunctionGroupsDaoService.getBranchFunctionGroups();
    this.branchFunctionGroups$.next(remaining);
  }

  /**
   * Creates a new, purely local Branch<->FunctionGroup mapping (not sent to the backend).
   */
  public async createBranchFunctionGroup(entry: BranchFunctionGroup): Promise<BranchFunctionGroup> {
    const created = await this.branchFunctionGroupsDaoService.createBranchFunctionGroup(entry);
    if (created) {
      this.branchFunctionGroups$.next([...this.branchFunctionGroups$.getValue(), created]);
    }
    return created;
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.clear();
    await this.loadRemoteBranchFunctionGroups(apiLevel, syncKey);
  }

  public async presync(): Promise<void> {}
  public async postsync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      await this.loadLocalBranchFunctionGroups();
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message,
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  async initializeCompleted(): Promise<void> {}

  // Private Functions
  private async loadLocalBranchFunctionGroups(): Promise<void> {
    try {
      this.logger.log(this.constructor.name, '- Load local branch function groups');
      const entries = await this.branchFunctionGroupsDaoService.getBranchFunctionGroups();
      this.branchFunctionGroups$.next(entries);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local branch function groups: ',
        error?.message,
      );
      throw error;
    }
  }

  private async loadRemoteBranchFunctionGroups(apiLevel: APILevel, syncKey: string): Promise<void> {
    if (!this.authenticationService.authenticationState.getValue()) {
      return;
    }

    if (apiLevel < APILevel.PROJECT_CONTACT_DIRECTORY) {
      this.logger.log(
        this.constructor.name,
        '- Backend API is too low to sync branch function groups',
      );
      return;
    }

    this.logger.log(this.constructor.name, '- Load new branch function groups from IMS');

    try {
      const branchIdsStr = this.branchesService.branches$
        .getValue()
        .map((branch) => branch.getImsId())
        .join(',');

      const entriesData = await this.remoteActionService.sendRemoteAction(
        new RemoteAction({
          endpoint: 'syncbranchfunctiongroups',
          cachable: false,
          fifo: false,
          authenticationRequired: true,
          data: {
            branchidsstr: branchIdsStr,
            synckey: syncKey,
          },
        }),
      );

      const entries: BranchFunctionGroup[] = entriesData.map((dto: any) =>
        BranchFunctionGroup.fromDto(dto),
      );

      this.logger.log(
        this.constructor.name,
        'Finished loading branch function groups (' + entries.length + ' Total)',
      );

      await this.branchFunctionGroupsDaoService.saveBranchFunctionGroups(entries);
      // Reload the full merged list (synced + local) so locally created mappings stay visible.
      const merged = await this.branchFunctionGroupsDaoService.getBranchFunctionGroups();
      this.branchFunctionGroups$.next(merged);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error downloading branch function groups: ',
        error?.message,
      );
      throw error;
    }
  }
}
